import React, { FC, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  Select,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { palette } from '../../theme';
import { getEquipmentPerformanceLabel, getRaceResultOrdinal } from '../../helpers/general.helpers';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import useService from '../../hooks/service.hook';
import EquipmentService from '../../services/equipments.service';
import type { RaceLogEntry } from '../../models/race.models';
import type { UpdateRaceLogPayload } from '../../models/race.models';

interface EquipmentOption {
  id: number | null;
  name: string;
}

const NA_EQUIPMENT_OPTION: EquipmentOption = { id: null, name: 'N/A' };

const PERFORMANCE_OPTIONS = [1, 2, 3, 4, 5].map((value) => ({
  value,
  label: getEquipmentPerformanceLabel(value),
}));

const NA_PERFORMANCE_VALUE = 'na';

const PERFORMANCE_OPTIONS_WITH_NA = [
  { value: NA_PERFORMANCE_VALUE, label: 'N/A' },
  ...PERFORMANCE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label })),
];

function orNa<T>(value: T | undefined | null): T | string {
  if (value === undefined || value === null || value === '') return 'N/A';
  return value;
}

interface EditFormState {
  race_type: string;
  result: number | '';
  equipment_id: number | null;
  equipment_performance: number | null;
  race_note: string;
}

function initialEditFormFromEntry(entry: RaceLogEntry): EditFormState {
  return {
    race_type: entry.race_type ?? '',
    result: entry.result ?? '',
    equipment_id: null,
    equipment_performance: entry.equipment_performance ?? null,
    race_note: entry.race_note ?? '',
  };
}

export const ROW_GRID = {
  display: 'grid' as const,
  gridTemplateColumns: '1fr 0.7fr 1fr 1fr 1fr 2fr auto',
  gap: 1,
  alignItems: 'center',
  minWidth: 0,
};

export interface RaceLogEntryRowProps {
  entry: RaceLogEntry;
  memberId?: number;
  onEdit?: (entryId: number, payload: UpdateRaceLogPayload) => void;
  onDelete?: (entryId: number) => void;
}

const RaceLogEntryRow: FC<RaceLogEntryRowProps> = ({ entry, memberId, onEdit, onDelete }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState>(() => initialEditFormFromEntry(entry));
  const [equipmentOptions, setEquipmentOptions] = useState<EquipmentOption[]>([]);
  const [equipmentLoading, setEquipmentLoading] = useState(false);
  const equipmentService = useService(EquipmentService);
  const { showSnackbar } = useSnackbarHelper();

  useEffect(() => {
    if (!isEditing) return;
    setEquipmentLoading(true);
    equipmentService
      .getEquipments(memberId)
      .then((res) => {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : raw?.results ?? [];
        const options: EquipmentOption[] = (list as { id?: number; name?: string; equipment_name?: string }[]).map(
          (item) => ({ id: item.id ?? 0, name: item.name ?? item.equipment_name ?? '' })
        );
        setEquipmentOptions(options);
      })
      .catch(() => {
        setEquipmentOptions([]);
      })
      .finally(() => {
        setEquipmentLoading(false);
      });
  }, [isEditing, equipmentService, memberId]);

  useEffect(() => {
    if (!isEditing || equipmentOptions.length === 0 || !entry.equipment_name?.trim()) return;
    setEditForm((f) => {
      if (f.equipment_id != null) return f;
      const found = equipmentOptions.find((o) => o.id != null && o.name === entry.equipment_name);
      return found ? { ...f, equipment_id: found.id } : f;
    });
  }, [isEditing, equipmentOptions, entry.equipment_name]);

  const resultDisplay = getRaceResultOrdinal(entry.result);
  const performanceDisplay = getEquipmentPerformanceLabel(entry.equipment_performance);

  const showMenu = entry.can_edit === true && Boolean(onEdit || onDelete);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleEditClick = () => {
    if (entry.can_edit !== true) return;
    setEditForm(initialEditFormFromEntry(entry));
    setIsEditing(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (entry.can_edit !== true) return;
    onDelete?.(entry.id);
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  const handleSave = () => {
    if (entry.can_edit !== true || !onEdit) return;
    if (editForm.result === '') {
      showSnackbar('Please fill in result.', 'warning');
      return;
    }
    if (!editForm.race_type.trim()) {
      showSnackbar('Please enter race type.', 'warning');
      return;
    }
    onEdit(entry.id, {
      race_type: editForm.race_type.trim(),
      result: Number(editForm.result),
      race_note: editForm.race_note.trim(),
      equipment_id: editForm.equipment_id,
      equipment_performance: editForm.equipment_performance,
    });
    setIsEditing(false);
  };

  const handleCancelEdit = () => setIsEditing(false);

  const cellSx = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  if (isEditing) {
    return (
      <Box
        sx={{
          padding: 1.5,
          marginBottom: 1,
          backgroundColor: palette.background.paper,
          borderRadius: 1,
          border: `1px solid ${palette.grey[200]}`,
        }}
      >
        <Box sx={ROW_GRID}>
          <Box sx={cellSx}>
            <Typography variant="body2">{orNa(entry.athlete_name)}</Typography>
          </Box>
          <Box sx={cellSx}>
            <TextField
              size="small"
              placeholder="e.g. 14"
              type="number"
              inputProps={{ min: 1 }}
              value={editForm.result === '' ? '' : editForm.result}
              onChange={(e) => {
                const v = e.target.value;
                setEditForm((f) => ({ ...f, result: v === '' ? '' : parseInt(v, 10) }));
              }}
              variant="outlined"
              fullWidth
            />
          </Box>
          <Box sx={cellSx}>
            <TextField
              size="small"
              placeholder="e.g. 5km Mass Start"
              value={editForm.race_type}
              onChange={(e) => setEditForm((f) => ({ ...f, race_type: e.target.value }))}
              variant="outlined"
              fullWidth
            />
          </Box>
          <Box sx={cellSx}>
            {!equipmentLoading && equipmentOptions.length === 0 ? (
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                No equipment available
              </Typography>
            ) : (
              <Autocomplete<EquipmentOption>
                size="small"
                options={[NA_EQUIPMENT_OPTION, ...equipmentOptions]}
                getOptionLabel={(opt) => opt.name}
                value={
                  editForm.equipment_id == null
                    ? NA_EQUIPMENT_OPTION
                    : equipmentOptions.find((o) => o.id === editForm.equipment_id) ?? NA_EQUIPMENT_OPTION
                }
                onChange={(_, value) => setEditForm((f) => ({ ...f, equipment_id: value?.id ?? null }))}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                loading={equipmentLoading}
                renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Equipment" fullWidth />}
              />
            )}
          </Box>
          <Box sx={cellSx}>
            <FormControl size="small" variant="outlined" fullWidth>
              <InputLabel id={`row-performance-edit-${entry.id}`}>Performance</InputLabel>
              <Select
                labelId={`row-performance-edit-${entry.id}`}
                label="Performance"
                value={editForm.equipment_performance == null ? NA_PERFORMANCE_VALUE : editForm.equipment_performance}
                onChange={(e) => {
                  const v = e.target.value;
                  setEditForm((f) => ({
                    ...f,
                    equipment_performance: v === NA_PERFORMANCE_VALUE || v === '' ? null : Number(v),
                  }));
                }}
              >
                {PERFORMANCE_OPTIONS_WITH_NA.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ ...cellSx, gridColumn: '6 / 8' }}>
            <TextField
              size="small"
              multiline
              minRows={2}
              placeholder="Race note..."
              value={editForm.race_note}
              onChange={(e) => setEditForm((f) => ({ ...f, race_note: e.target.value }))}
              variant="outlined"
              fullWidth
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant="outlined" size="small" onClick={handleCancelEdit}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        padding: 1,
        marginBottom: 1,
        backgroundColor: palette.background.paper,
        borderRadius: 1,
        border: `1px solid ${palette.grey[200]}`,
        ...ROW_GRID,
      }}
    >
      {showMenu && (
        <>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 1,
              padding: '2px',
              gridColumn: 7,
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <MoreVertIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {onEdit && (
              <MenuItem onClick={handleEditClick} sx={{ fontSize: 14 }}>
                <EditIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                Edit
              </MenuItem>
            )}
            {onDelete && (
              <MenuItem onClick={handleDeleteClick} sx={{ fontSize: 14, color: 'error.main' }}>
                <DeleteIcon sx={{ fontSize: 16, mr: 1 }} />
                Delete
              </MenuItem>
            )}
          </Menu>
        </>
      )}
      <Box sx={{ ...cellSx, pr: 4 }}>
        <Typography variant="body2" noWrap title={String(entry.athlete_name ?? '')}>
          {orNa(entry.athlete_name)}
        </Typography>
      </Box>
      <Box sx={cellSx}>
        <Typography variant="body2">{resultDisplay}</Typography>
      </Box>
      <Box sx={cellSx}>
        <Typography variant="body2" noWrap title={String(entry.race_type ?? '')}>
          {orNa(entry.race_type)}
        </Typography>
      </Box>
      <Box sx={cellSx}>
        <Typography variant="body2" noWrap title={String(entry.equipment_name ?? '')}>
          {orNa(entry.equipment_name)}
        </Typography>
      </Box>
      <Box sx={cellSx}>
        <Typography variant="body2" fontWeight={600}>
          {performanceDisplay}
        </Typography>
      </Box>
      <Box sx={{ ...cellSx, pr: 4 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {orNa(entry.race_note)}
        </Typography>
      </Box>
      <Box sx={{ gridColumn: 7 }} />

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle id="delete-race-log-row-dialog-title">Delete race log entry?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-race-log-row-dialog-description">
            Are you sure you want to delete this race log entry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RaceLogEntryRow;
